import { Component, ViewChild, OnInit, OnDestroy, HostListener, Injector, ElementRef } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { Subscription, of, BehaviorSubject, filter } from 'rxjs';
import { MessageService, MenuItem } from 'primeng/api';
import moment from 'moment-timezone';
import { GridsterPush, GridsterPushResize } from 'angular-gridster2';

import { ReportModel } from '../../models/report.model';
import { AttributeModel } from '../../../Misc/models/attribute.model';
import { ReportsPageBuilderService } from '../../services/reports-page-builder.service';
import { BlockModel } from '../../../PageBuilder/models/block.model';
import { ReportsService } from '../../services/reports.service';
import { ReportsSeriesService } from '../../services/reports-series.service';
import { ChartDisplayElementModel } from '../../models/chart-display-element.model';
import { reportAttributeTypesEnum } from '../../enums/reportAttributeTypes.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { attributeTypesEnum } from '../../../Misc/enums/attribute-types.enum';
import { TimeHelper } from '../../../Forms/helpers/time.helper';
import { ImageUploadFieldModel } from '../../../PageBuilder/models/image-upload-field.model';
import { ReportErrorsModel } from '../../models/report-errors.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { ReportsSeriesPreviewService } from '../../services/report-series-preview.service';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { TextInputComponent } from '../../../Forms/components/TextInput/text-input.component';
import { ChartSetupDialogComponent } from '../ChartSetupDialog/chart-setup-dialog.component';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { DateRangeFieldModel } from '../../models/date-range-field.model';
import { ReportSeriesModel } from '../../models/report-series.model';
import { ConfirmDialogModel } from '../../../Dialogs/models/confirm-dialog.model';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { ConfirmDialogComponent } from '../../../Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { DateQueryModel } from '../../../Forms/models/date-query.model';
import { ReportDateRangeModel } from '../../models/report-date-range.model';
import {
    getMonitoringDateQueryInputFolderOptions,
    getMonitoringDateQueryInputTopicOptions
} from '../../../../Monitor/Shared/models/monitoring-date-query-input-options';
import { MonitorConfigService } from '../../../MonitorShared/services/monitor-config.service';
import { SearchRangeModel } from '../../../../Monitor/Core/models/search-range.model';
import { SeriesPickerFieldModel } from '../../models/series-picker-field.model';
import { socialSeriesFilterTypes } from '../../enums/series-filter-types.enum';
import { mentionSeriesTypes, SeriesTypeEnum, socialSeriesTypes } from '../../enums/series-type.enum';
import { SeriesPickerCompareFieldModel } from '../../models/series-picker-compare-field.model';
import { coverageOverTimeComparisonBlockType } from '../../models/reports-block-types.model';
import { AbstractPageBuilderService } from '../../../PageBuilder/services/abstract-page-builder.service';
import { UpdateGlobalSourceDialogComponent, UpdateGlobalSourceOptions } from '../updateGlobalSourceDialog/update-global-source-dialog.component';
import { UpdateGlobalSourceDialogModel, UpdateGlobalSourceDialogResultModel } from '../../models/update-global-source-dialog.model';
import { SeriesStateEnum } from '../../enums/series-state.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { GenerateReportDialogComponent } from '../GenerateReportDialog/generate-report-dialog.component';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { CurrencyConversionService } from '../../../../Core/Users/services/currency-conversion.service';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { getDefaultDateInputOptionValuesModel } from '../../../Forms/models/date-query-input-options.model';
import { ActionKeys } from '../../../Forms/enums/keys.enum';
import { defaultDateTypeOptions, savedSourceDateTypeOptions } from '../../../Forms/components/DateRangeSelector/date-range-selector.component';
import { reportSavedSourceTypes } from '../SeriesPickerField/series-picker-field.component';
import { RightSidebarComponent } from '../../../Layout/components/RightSidebar/right-sidebar.component';
import { NewResourceDialogModel } from '../../../Dialogs/models/new-resource-dialog.model';
import { NewResourceDialogComponent } from '../../../Dialogs/components/NewResourceDialog/new-resource-dialog.component';
import { DisplayGroupModel } from '../../../Layout/models/display-group.model';
import { CachedDateService } from '@Common/MonitorShared/services/cached-date.service';

@Component({
    selector: 'agpr-report',
    templateUrl: './report.component.html',
    styleUrls: ['./report.component.scss'],
    providers: [
        {
            provide: AbstractPageBuilderService,
            useExisting: ReportsPageBuilderService
        }
    ],
    standalone: false
})
export class ReportComponent implements OnInit, OnDestroy {

    readonly seriesStates = SeriesStateEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly detailsSidebarIndex: number = 1;

    readonly saveActions: MenuItem[] = [
        {
            label: this.localizationService.transform('Reports.button.changes'),
            command: () => this.saveReport()
        },
        {
            label: this.localizationService.transform('Reports.button.saveAs'),
            command: () => this.openSaveAsDialog()
        }
    ];

    id: string = 'report';
    report: ReportModel = new ReportModel();
    gridIsEmpty: boolean = true;
    attributes: AttributeModel[];

    activeSidebarTabIndex: number = null;

    public chartSetupDialogVisible: boolean = false;

    globalDateQuery: DateQueryModel;
    cachedDateQuery: DateQueryModel;

    dateOptions = getDefaultDateInputOptionValuesModel();
    titleEditing = false;

    public globalSingleField: SeriesPickerFieldModel = new SeriesPickerFieldModel(
        null,
        'series',
        '',
        '',
        false,
        undefined,
        [
            ...mentionSeriesTypes,
            ...socialSeriesTypes
        ],
        socialSeriesFilterTypes
    );

    public globalSingleSeries: ReportSeriesModel[] = [new ReportSeriesModel()];

    public globalMultiSeriesField: SeriesPickerCompareFieldModel = new SeriesPickerCompareFieldModel(
        null,
        'multiSeries',
        '',
        '',
        [
            ...mentionSeriesTypes,
            ...socialSeriesTypes
        ],
        socialSeriesFilterTypes,
        true
    );

    public globalMultiSeries: ReportSeriesModel[] = [new ReportSeriesModel()];
    public globalMultiSeriesBlock: BlockModel = new BlockModel(undefined, coverageOverTimeComparisonBlockType);

    public hasMultiSeries: boolean = false;
    public hasSingleSource: boolean = false;

    public showErrorsLevel: number;
    public draftErrors: number;
    searchRange: SearchRangeModel = null;
    dateTypeOptions:  DropdownOptionModel[] = defaultDateTypeOptions;
    reportHasFolder: boolean;
    public globalSeriesState: SeriesStateEnum = SeriesStateEnum.none;

    dialogID: string;

    public errors: ReportErrorsModel = new ReportErrorsModel();

    public imageUploadBlock = new ImageUploadFieldModel(
        null,
        'url',
        'Reports.imageUpload.uploadLabel',
        'Reports.imageUpload.uploadDescription',
        1000000,
        'image/gif, image/jpeg, image/png, image/jpg',
        'Reports.imageUpload.buttonLabel'
    );

    public dateRangeField: DateRangeFieldModel = new DateRangeFieldModel(
        null,
        'date',
        'Reports.formField.dateRange.label',
        ''
    );

    /** Max number of characters for each of the main report form fields */
    public readonly maxInputTextLength: number = 256;
    fieldErrors: {[key: string]: boolean} = {
        nameEmpty: false,
        nameLength: false
    };

    public gridWidth: number = 0;
    public overlayContainer: Element;

    public saveLoading: boolean = false;
    public invalidReportOrBlockId: boolean = true;

    public preferences: PreferencesModel;

    currencyOptions: DropdownOptionModel[] = [];
    selectedCurrencyOption: DropdownOptionModel;
    suggestions$: BehaviorSubject<DropdownOptionModel[]> = new BehaviorSubject<DropdownOptionModel[]>([]);

    private subscriptions: Subscription[] = [];

    isDirty: boolean = false;

    generateReportButtonTooltip: string = '';

    @ViewChild('titleField', { static: true }) titleField: TextInputComponent;
    @ViewChild('rightSidebar') rightSidebar: RightSidebarComponent;

    constructor(
        protected injector: Injector,
        public pageBuilderService: ReportsPageBuilderService,
        public coreDialogService: CoreDialogService,
        public reportsService: ReportsService,
        public reportsSeriesService: ReportsSeriesService,
        public reportSeriesPreviewService: ReportsSeriesPreviewService,
        public localizationService: LocalizationService,
        public timeHelper: TimeHelper,
        public currentUserStateService: CurrentUserStateService,
        public monitorConfigService: MonitorConfigService,
        private messageService: MessageService,
        private ref: ElementRef,
        public titleService: Title,
        private currencyConversionService: CurrencyConversionService,
        private router: Router,
        private cachedDateService: CachedDateService
    ) { }

    ngOnInit(): void {
        this.overlayContainer = document.getElementById('main-content');
        this.subscriptions = [];

        // should actually be getting the report from the report service when it exists
        this.subscriptions.push(
            this.pageBuilderService.getBlocks().subscribe(
                (blocks) => {
                    this.gridIsEmpty = !blocks || blocks.length < 1;
                }
            ),
            this.pageBuilderService.getSelectedBlock().subscribe(
                (block) => {
                    if (this.chartSetupDialogVisible) {
                        return;
                    }

                    if (block) {
                        this.chartSetupDialogVisible = true;
                        this.pageBuilderService.selectBlock(block);
                        const animationTrigger = new BehaviorSubject(
                            new FullProfileAnimationModel(
                                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                                OverlayAnimationStateEnum.open
                            )
                        );
                        const ioModel =  {
                            inputs: {
                                animationTrigger: animationTrigger,
                                overlayContainer: this.overlayContainer,
                                editState: true
                            },
                            outputs: {
                                dialogClose: (closeAction) => {
                                    this.chartSetupDialogVisible = false;
                                }
                            }
                        };
                        this.coreDialogService.open(ChartSetupDialogComponent, ioModel, this.injector);
                    }
                }
            ),
            this.reportsService.getCurrentReport().subscribe(
                report => {
                    if (!report) {
                        // @TODO Show loading
                        return;
                    }

                    this.report = report;
                    this.isDirty = !this.report.equals(this.reportsService.getCachedCurrentReport());
                    this.generateReportButtonTooltip = this.isDirty ?
                        this.localizationService.transform('Reports.button.tooltip.dataError.generate') : this.generateReportButtonTooltip;

                    let hasInvalidBlockId = false;
                    this.report.blocks.forEach(block => {
                        if (!block.id) {
                            hasInvalidBlockId = true;
                        }
                    });

                    this.invalidReportOrBlockId = !report || !report.id || hasInvalidBlockId;

                    this.hasSingleSource = this.report.hasSingleSeries();
                    this.hasMultiSeries = this.report.hasMultiSeries();

                    // if the report has only one single source chart, then set the global single source the same as the chat
                    if (this.hasSingleSource) {
                        const blocks: BlockModel[] = this.report.getSingleSourceCharts();

                        if (blocks.length === 1) {
                            this.report.globalSourceSingleSeries = (blocks[0].displayElement as ChartDisplayElementModel)
                                .series[0].clone();
                            this.report.globalSourceSingleSeries.resetSearchCriteriaId();
                        }
                    }

                    // if the report has only one multi source chart, then set the global multi source the same as the chat
                    if (this.hasMultiSeries) {
                        const blocks: BlockModel[] = this.report.getMultiSourceCharts();

                        if (blocks.length === 1) {
                            this.report.globalSourceMultiSeries = (blocks[0].displayElement as ChartDisplayElementModel)
                                .series.map(seriesItem => {
                                    const clone = seriesItem.clone();
                                    clone.resetSearchCriteriaId();
                                    return clone;
                                });
                        }
                    }

                    if (this.report.globalSourceSingleSeries) {
                        this.globalSingleSeries = [this.report.globalSourceSingleSeries.clone()];
                    }

                    if (this.report.globalSourceMultiSeries?.length > 0) {
                        this.globalMultiSeries = this.report.globalSourceMultiSeries.map(series => series.clone());
                    }

                    this.validateGlobalSource();

                    const isGlobalDateSet = this.report.isGlobalDateSet();

                    if (isGlobalDateSet) {
                        this.globalDateQuery = this.report.getSeries()[0].dateRange;
                    } else if (isGlobalDateSet === null || isGlobalDateSet === undefined) {
                        this.globalDateQuery = DateQueryModel.getLast24HoursQuery();
                    } else {
                        this.globalDateQuery = null;
                    }

                    if (this.report.anySeriesAllowsAllDate()) {
                        this.dateOptions = getMonitoringDateQueryInputFolderOptions();
                        this.reportHasFolder = true;
                    } else {
                        this.dateOptions = getMonitoringDateQueryInputTopicOptions();
                        this.reportHasFolder = false;
                    }

                    this.setReportAttributes();
                    this.gridWidth = 0;
                    this.updateGridWidth();

                    const title =
                    report.name ?
                        this.localizationService.transform('Reports.browserTitle.name', {name: report.name}) :
                        this.localizationService.transform('Reports.browserTitle.new');
                    this.titleService.setTitle(title);

                    this.prepareReportCurrency();

                }
            ),
            this.reportsSeriesService.getGlobalState().subscribe(
                state => {
                    this.globalSeriesState = state;
                    if (this.globalSeriesState === this.seriesStates.loading) {
                        this.generateReportButtonTooltip = this.localizationService.transform('Reports.button.tooltip.waitingForData');
                    } else if (this.globalSeriesState === this.seriesStates.error) {
                        this.generateReportButtonTooltip = this.localizationService.transform('Reports.button.tooltip.dataError.share');
                    } else {
                        this.generateReportButtonTooltip = '';
                    }
                }
            ),
            this.monitorConfigService.getSearchRange().subscribe(
                searchRange => {
                    this.searchRange = searchRange;
                }
            ),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(preferences => {
                this.preferences = preferences;
                this.prepareUserCurrency();
            }),
            this.cachedDateService.getCachedSelectedDate().subscribe(
                cachedDate => {
                    this.cachedDateQuery = cachedDate;
                }
            ),
        );
    }


    ngOnDestroy(): void {
        this.subscriptions.forEach(
            subscription => {
                subscription.unsubscribe();
            }
        );
    }

    @HostListener('window:beforeunload', ['$event'])
    pageUnload($event) {
        if (!this.reportsService.canLeaveCurrentReport()) {
            $event.returnValue = this.localizationService.transform('DeactivateGuard.messageText');
        }
    }

    private prepareUserCurrency(){

        this.currencyOptions = this.currencyConversionService.getCurrencyDropdownOptions();
        this.suggestions$.next( this.currencyOptions);
        const userCurrency = this.preferences.currency;
        this.selectedCurrencyOption = this.currencyOptions.find(option => option.value === userCurrency);

    }

    selectCurrency(){
        // update the currency settings on both the draft global series and report global series
        if (this.globalSingleSeries){
            this.globalSingleSeries[0].styles.currency = this.selectedCurrencyOption.value;
            this.globalSingleSeries[0].styles.currencyModified = true;
        }

        if (this.globalMultiSeries){
            this.globalMultiSeries.forEach(_series => {
                _series.styles.currency = this.selectedCurrencyOption.value;
                _series.styles.currencyModified = true;
            });
        }

        if (this.report.globalSourceSingleSeries){
            this.report.globalSourceSingleSeries.styles.currency = this.selectedCurrencyOption.value;
            this.report.globalSourceSingleSeries.styles.currencyModified = true;
        }

        if (this.report.globalSourceMultiSeries){
            this.report.globalSourceMultiSeries.forEach(_series => {
                _series.styles.currency = this.selectedCurrencyOption.value;
                _series.styles.currencyModified = true;
            });
        }

        // apply the currency on all the blocks of the report.
        this.report.blocks.forEach( _block => {
            _block.displayElement.styles.currency = this.selectedCurrencyOption.value;
        });

        this.updateBlocks();
    }

    /**
     * Fixes the width of the contents of the scrollable area so that the charts don't get cut off
     * @HACK we should see if updating gridster fixes this (when we upgrade angular)
     */
    private updateGridWidth(): void {
        // Requesting a frame fixes an issue where gridster updates the positions after the initial check
        // So we don't get a "expression changed after check" error
        requestAnimationFrame(() => {
            const gridDimensions = this.report.getDimensions();

            if (gridDimensions.width) {
                const gridUnitWidth = 115 + 20; // unit width + padding
                const scrollFix = 20; // extra pixels to add to the width of the gridster area to fix the scroll

                this.gridWidth = (gridUnitWidth * gridDimensions.width) + scrollFix;
            }
        });
    }

    /**
     * Process a save request from the Save button
     */
    public processSaveAction(): void {
        if (this.report.id) {
            this.saveReport();
        } else {
            const inputConfig: NewResourceDialogModel = {
                inputs: {
                    id: 'reports.saveReportDialog',
                    width: 300, // so it doesn't get wider when an error shows up
                    heading: 'Reports.input.saveReportHeader',
                    description: 'Reports.input.saveReportMessage',
                    placeholder: 'Reports.input.saveReportHint',
                    okLabel: 'Reports.input.save',
                    cancelLabel: 'Monitoring.cancelButtonLabel',
                    formValidator: (text: string) => {
                        this.report.name = text;

                        if (this.report.name === null || this.report.name === undefined || this.report.name.trim().length === 0) {
                            return ['Report.input.saveErrorMessage'];
                        }

                        if (!this.reportsService.validateReportName(this.report.name)) {
                            return ['Reports.save.nameLenghError'];
                        }

                        if (!this.reportsService.isDuplicateName(this.report.name)) {
                            return ['Reports.saveReportDuplicateNameErrorMessage'];
                        }

                        return [];
                    },
                    submit: (text: string, group?: DisplayGroupModel) => {
                        this.report.groupId = group?.id;
                        this.saveReport();

                        return of(true);
                    }
                }
            };

            this.coreDialogService.open(NewResourceDialogComponent, inputConfig);
        }
    }

    /**
     * Process a save request from new report save dialog
     */
    public saveReport(): void {
        this.saveLoading = true;
        this.reportsService.saveCurrentReport()
            .subscribe({
                next: () => this.saveLoading = false,
                error: () => this.saveLoading = false,
            });
    }

    /**
     * Open save as dialog, prompting user for new report name
     */
    openSaveAsDialog() {
        const io: NewResourceDialogModel = {
            inputs: {
                id: `${this.id}.saveAsReport.dialog`,
                heading: 'Reports.dialog.saveAs.title',
                description: 'Reports.dialog.saveAs.description',
                placeholder: 'Reports.dialog.saveAs.placeholder',
                okLabel: 'Reports.button.save',
                currentGroup: this.report.groupId,
                formValidator: (newName: string) => {
                    if (newName === null || newName === undefined || newName.length === 0) {
                        return ['Report.input.saveErrorMessage'];
                    }

                    if (!this.reportsService.validateReportName(newName)) {
                        return ['Reports.save.nameLenghError'];
                    }

                    if (!this.reportsService.isDuplicateName(newName)) {
                        return ['Reports.saveReportDuplicateNameErrorMessage'];
                    }

                    return [];
                },
                submit: (newName: string, group?: DisplayGroupModel) => {
                    this.report.groupId = group?.id;
                    this.saveAsReport(newName);
                    return of(null);
                },
            }
        };
        this.coreDialogService.open(NewResourceDialogComponent, io);
    }


    saveAsReport(newName: string) {
        const newReport: ReportModel = this.report.clone(true, true);
        newReport.name = newName;

        this.saveLoading = true;

        this.reportsService.createNewReport(newReport).subscribe({
            next: (reportId: number) => {
                this.saveLoading = false;
                this.router.navigate(['Report/reports/report/', reportId]);
            },
            error: () => this.saveLoading = false,
        });

    }

    /**
     * Handle saving image URL for exported PDF report
     */
    public imageUploaded(imageUrl: string) {
        this.report.exportImageUrl = imageUrl;
        this.reportsService.setCurrentReport(this.report);
    }

    /**
     * Clears the cover image.
     */
    public clearCoverImage() {
        // Requesting an animation frame so that the "delete" icon is still rendered when clickOutside is checked. Without
        // it, clicking the "delete" icon is counted as clicking outside of the right hand panel and the panel is closed.
        requestAnimationFrame(() => {
            this.report.exportImageUrl = '';
            this.reportsService.setCurrentReport(this.report);
        });

        // the 'description' underneath the image upload button is set to be the filename currently uploaded.
        // when the file is cleared, the 'description' will display description message instead of filename.
        this.imageUploadBlock.fileName = null;
    }

    setReportAttributes(): void {
        const name: AttributeModel = {
            id: reportAttributeTypesEnum.name,
            label: 'name',
            value: this.report.name,
            type: attributeTypesEnum.simple
        };

        const createdBy: AttributeModel = {
            id: reportAttributeTypesEnum.createdBy,
            label: 'createdBy',
            value: this.report.userId,
            type: attributeTypesEnum.user
        };

        const createdDate: AttributeModel = {
            id: reportAttributeTypesEnum.createdDate,
            label: 'dateCreated',
            value: this.report.createdDate,
            type: attributeTypesEnum.date,
        };

        const lastModifiedBy: AttributeModel = {
            id: reportAttributeTypesEnum.modifiedBy,
            label: 'lastModifiedBy',
            value: this.report.modifiedByUserId,
            type: attributeTypesEnum.user
        };

        const lastModifiedDate: AttributeModel = {
            id: reportAttributeTypesEnum.modifiedDate,
            label: 'lastModifiedDate',
            value: this.report.modifiedDate,
            type: attributeTypesEnum.date,
        };

        this.attributes = [
            name,
            createdBy,
            createdDate,
            lastModifiedBy,
            lastModifiedDate
        ].filter(x => !!x.value);
    }

    nameChanged(value: string): void {
        this.fieldErrors.nameEmpty = !value || value.trim().length === 0;
        this.fieldErrors.nameLength = value && value.length === this.maxInputTextLength;
    }

    titleChanged(value: string): void {
        this.fieldErrors.titleEmpty = !value || value.trim().length === 0;
        this.fieldErrors.titleLength = value && value.length === this.maxInputTextLength;
    }

    selectEditTitleField(): void {
        this.titleEditing = true;
    }

    keyDown(event): void {
        if (event.key === ActionKeys.ENTER || event.key === ActionKeys.ESCAPE) {
            this.titleEditing = false;
        }
    }

    addChartClick(): void {
        this.chartSetupDialogVisible = true;
        const block = new BlockModel();
        this.pageBuilderService.selectBlock(block);

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const isCachedDateAvailable =
            this.cachedDateQuery && (
                this.cachedDateQuery.type === DateQueryTypeEnum.ABSOLUTE ||
                this.dateOptions.some(({ value }) =>
                    DateQueryModel.equals(this.cachedDateQuery, value.query)
                )
            );

        const ioModel =  {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                globalDateQuery: isCachedDateAvailable ? this.cachedDateQuery : this.globalDateQuery
            },
            outputs: {
                dialogClose: (closeAction) => {
                    this.chartSetupDialogVisible = false;
                }
            }
        };

        this.coreDialogService.open(ChartSetupDialogComponent, ioModel, this.injector);
    }

    showSharedReportCreateDialog(): void {

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const ioModel =  {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                report: this.report
            }
        };
        this.coreDialogService.open(GenerateReportDialogComponent, ioModel, this.injector);
    }

    /**
     * Checks if the report model is properly defined
     */
    private hasReport(): boolean {
        const hasReport: boolean = this.report !== null && typeof this.report === 'object';

        return hasReport;
    }

    /**
     * Checks if the name is filled out, and toggles an error
     * based on the response
     *
     * @returns TRUE if validation passes, false otherwise
     */
    public validateName(): boolean {
        const hasReport: boolean = this.hasReport();
        const hasName: boolean = hasReport && this.report.hasName();

        this.errors.emptyReportName = !hasName;

        return !this.errors.emptyReportName;
    }

    public refreshAll() {
        this.reportsSeriesService.refreshAll();
        this.reportsService.loadMentions(this.report);
    }

    globalDateQueryChange(dateQuery: DateQueryModel) {
        if (this.dialogID) {
            return;
        }
        let selectedDateInValidRange = true;
        if (dateQuery.absoluteDate) {
            dateQuery.absoluteDate = moment(dateQuery.absoluteDate).startOf('minute').toDate();
            selectedDateInValidRange = dateQuery.absoluteDate >= this.searchRange.from;
        } else if (dateQuery.absoluteRange) {
            dateQuery.absoluteRange.start = moment(dateQuery.absoluteRange.start).startOf('minute').toDate();
            dateQuery.absoluteRange.end = moment(dateQuery.absoluteRange.end).endOf('minute').toDate();
            selectedDateInValidRange = dateQuery.absoluteRange.start >= this.searchRange.from;
        }

        const confirmDialogModel: ConfirmDialogModel = {
            inputs: {
                id: this.id,
                heading: 'Reports.sidebar.settings.dateRange.confirm.header',
                showCancel: true,
                message: 'Reports.sidebar.settings.dateRange.confirm.message.updateAll',
                okLabel: 'Reports.sidebar.settings.dateRange.confirm.okLabel'
            },
            outputs: {
                dialogClose: (confirm: boolean) => {
                    if (confirm) {
                        this.reportsSeriesService.setGlobalDateRange(new ReportDateRangeModel().setFromDateQuery(dateQuery), !selectedDateInValidRange);
                        this.updateBlocks();
                    } else {
                        this.fallbackGlobalDateQuery();
                    }

                    this.dialogID = null;
                }
            }
        };

        const incompatibleCharts = [];
        switch (dateQuery.type) {
            case DateQueryTypeEnum.RELATIVE:
                // referral trend chart cannot use last 24 hours
                if (dateQuery.relativeStartOffsetH === -24) {
                    incompatibleCharts.push(...this.report.getChartsThatCanNotUseLast24Hours());
                }
                break;
            case DateQueryTypeEnum.ABSOLUTE:
                if (!selectedDateInValidRange) {
                    incompatibleCharts.push(...this.report.getChartsThatHaveUnsavedSources());
                }
                break;
            case DateQueryTypeEnum.RELATIVE_TIMEZONE:
                // on prod the search range for unsaved sources is longer than last three months, so no need to check here
                break;
            case DateQueryTypeEnum.ALL:
                incompatibleCharts.push(...this.report.getChartsThatCanNotUseAllDate());
                break;
        }
        if (incompatibleCharts.length > 0) {
            if (incompatibleCharts.length === this.report.blocks.length) {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform('Reports.sidebar.settings.dateRange.allIncompatible.summary'),
                    detail: this.localizationService.transform('Reports.sidebar.settings.dateRange.allIncompatible.detail')
                });
                this.fallbackGlobalDateQuery();
                return;
            } else {
                confirmDialogModel.inputs.alertType = messageSeverityEnum.warning;
                confirmDialogModel.inputs.alertMessage = 'Reports.sidebar.settings.dateRange.confirm.warning.partialUpdate';
                confirmDialogModel.inputs.alertMessageList = incompatibleCharts.map(
                    chart => (chart.displayElement as ChartDisplayElementModel).title
                );
                confirmDialogModel.inputs.message = 'Reports.sidebar.settings.dateRange.confirm.message.partialUpdate';
            }
        }

        this.dialogID = this.coreDialogService.open(ConfirmDialogComponent, confirmDialogModel);
    }

    singleSeriesChanged(series: ReportSeriesModel[]): void {
        this.globalSingleSeries = series;
        this.validateGlobalSource();
        this.updateDateTypeOptions();
    }

    multiSeriesChanged(series: ReportSeriesModel[]): void {
        this.globalMultiSeries = series;
        this.validateGlobalSource();
        this.updateDateTypeOptions();
    }

    prepareReportCurrency(){
        if (!this.report.id && !(this.report.globalSourceSingleSeries?.styles.currencyModified ||
            this.report.globalSourceMultiSeries?.[0]?.styles.currencyModified)){
            // if the report is new and user did not modify currency, drop the currency from global source seires so that
            // the user preference currency will be used.
            delete this.report.globalSourceSingleSeries?.styles.currency;
            this.report.globalSourceMultiSeries?.forEach(_series => delete _series.styles.currency);
        }

        if (this.globalMultiSeries && this.globalMultiSeries[0].styles.currency){
            this.selectedCurrencyOption = this.currencyOptions.find(option => option.value === this.globalMultiSeries[0].styles.currency);
        }else if (this.globalSingleSeries && this.globalSingleSeries[0].styles.currency){
            this.selectedCurrencyOption = this.currencyOptions.find(option => option.value === this.globalSingleSeries[0].styles.currency);
        }else if (this.preferences){
            this.selectedCurrencyOption = this.currencyOptions.find(option => option.value === this.preferences.currency);
        }
    }

    fallbackGlobalDateQuery(): void {
        const isGlobalDateSet = this.report.isGlobalDateSet();
        if (isGlobalDateSet) {
            this.globalDateQuery = this.report.getSeries()[0].dateRange;
        } else if (isGlobalDateSet === null || isGlobalDateSet === undefined) {
            this.globalDateQuery = DateQueryModel.getLast24HoursQuery();
        } else {
            this.globalDateQuery = null;
        }
    }

    /**
     * Checks whether the global source is valid
     */
    private validateGlobalSource(): boolean {
        let isValid = true;

        if (this.hasSingleSource) {
            if (!this.globalSingleSeries[0].isValid()) {
                isValid = false;
            }
        }

        if (this.hasMultiSeries) {
            this.globalMultiSeries.map(seriesItem => {
                if (!seriesItem.isValid()) {
                    isValid = false;
                }
            });
        }
        return isValid;
    }

    /**
     * Checks for global variables and trigger the update of charts based on global variables
     */
    updateGlobalSource(): void {
        // Closes any open dropdown panels in Report Settings tab, fixes issue of open panels persisting after tab collapse
        document.body.click();

        if (!this.validateGlobalSource()) {
            // we only want to show in-line error when clicking apply button
            // so .next(false) to avoid newly added source picker from showing in-line error
            this.reportsSeriesService.showSourceValidateError$.next(true);
            this.reportsSeriesService.showSourceValidateError$.next(false);
            this.messageService.add({
                severity: messageSeverityEnum.error,
                summary: this.localizationService.transform('Reports.sidebar.settings.globalSource.growlError.summary'),
                detail: this.localizationService.transform('Reports.sidebar.settings.globalSource.growlError.message')
            });
            return;
        }

        if (this.report.blocks.length > 0) {
            if (this.dialogID) {
                return;
            }

            const updateGlobalSourceDialogModel: UpdateGlobalSourceDialogModel = {
                inputs: {
                    id: this.id,
                    message: 'Reports.sidebar.settings.globalSource.confirm.message.default'
                },
                outputs: {
                    dialogClose: async (result: UpdateGlobalSourceDialogResultModel) => {
                        if (result?.confirmed) {
                            await this.reportsSeriesService.resetReportSeries();
                            if (result.selectedOption === UpdateGlobalSourceOptions.updatePartial) {
                                this.reportsSeriesService.setGlobalMentionsSingleSeries(this.globalSingleSeries, false);
                                this.reportsSeriesService.setGlobalMentionsMultiSeries(this.globalMultiSeries, false);
                            } else {
                                this.reportsSeriesService.setGlobalMentionsSingleSeries(this.globalSingleSeries, true);
                                this.reportsSeriesService.setGlobalMentionsMultiSeries(this.globalMultiSeries, true);
                            }
                            this.report.globalSourceSingleSeries = this.globalSingleSeries[0].clone();
                            this.report.globalSourceMultiSeries = this.globalMultiSeries.slice();

                            this.updateBlocks();
                            this.rightSidebar.closeFocus();
                        }
                        this.dialogID = null;
                    }
                }
            };

            // get the list of incompatible charts
            const allIncompatibleChartTitles = this.getAllIncompatibleTitles();
            const incompatibleDefaultSourceTitles = this.getDefaultIncompatibleTitles();

            updateGlobalSourceDialogModel.inputs.alertType = messageSeverityEnum.warning;
            updateGlobalSourceDialogModel.inputs.alertMessage = 'Reports.sidebar.settings.globalSource.warning.message';
            updateGlobalSourceDialogModel.inputs.allAlertMessageList = allIncompatibleChartTitles;
            updateGlobalSourceDialogModel.inputs.defaultAlertMessageList = incompatibleDefaultSourceTitles;

            this.dialogID = this.coreDialogService.open(UpdateGlobalSourceDialogComponent, updateGlobalSourceDialogModel);
        } else {
            this.report.globalSourceSingleSeries = this.globalSingleSeries[0].clone();
            this.report.globalSourceMultiSeries = this.globalMultiSeries.slice();
            this.updateBlocks();
            this.rightSidebar.closeFocus();
        }

        if (this.globalSingleSeries){
            this.globalSingleSeries[0].styles.currency = this.selectedCurrencyOption.value;
        }

        if (this.globalMultiSeries){
            this.globalMultiSeries.forEach(_series => _series.styles.currency = this.selectedCurrencyOption.value);
        }
    }

    updateDateTypeOptions(): void {
        const series = [ ...this.globalSingleSeries, ...this.globalMultiSeries ];
        const areAllSavedSources = series.every(s => reportSavedSourceTypes.includes(s.type));
        this.dateTypeOptions = areAllSavedSources ? savedSourceDateTypeOptions : defaultDateTypeOptions;
    }

    /**
     * returns an array of titles of ALL the incompatible chart titles when trying to set global settings
     */
    private getAllIncompatibleTitles(): string[] {
        const singleSeriesList: string[] = this.report.getTitlesforIncompatibleSingleSeries(this.globalSingleSeries);
        const multiSeriesList: string[] = this.report.getTitlesforIncompatibleMultiSeries(this.globalMultiSeries);
        return [...singleSeriesList, ...multiSeriesList];
    }

    /**
     * returns an array of titles of ALL the incompatible chart titles for all the charts that are using the
     * default global values when trying to set global settings
     */
    private getDefaultIncompatibleTitles(): string[] {
        const singleSeriesList: string[] = this.report.getTitlesforDefaultSingleSeries();
        const multiSeriesList: string[] = this.report.getTitlesforDefaultMultiSeries();
        return [...singleSeriesList, ...multiSeriesList];
    }

    private updateBlocks(): void {
        this.report.blocks = this.report.blocks.map(block => block.clone());
        this.pageBuilderService.setBlocks(this.report.blocks);
    }

    public handleBlockKeyboardMove(event: {event: KeyboardEvent; block: BlockModel}): void {
        const dragHandle = document.activeElement as HTMLElement;
        const itemToPush = this.pageBuilderService.gridsterAPI.getItemComponent(event.block);
        const push = new GridsterPush(itemToPush);
        let direction: string;
        switch (event.event.key) {
            case ActionKeys.DOWN:
                direction = push.fromNorth;
                itemToPush.$item.y++;
                break;
            case ActionKeys.UP:
                direction = push.fromSouth;
                itemToPush.$item.y--;
                break;
            case ActionKeys.LEFT:
                direction = push.fromEast;
                itemToPush.$item.x--;
                break;
            case ActionKeys.RIGHT:
                direction = push.fromWest;
                itemToPush.$item.x++;
                break;
        }
        if (push.pushItems(direction)) {
            push.checkPushBack();
            push.setPushedItems();
            itemToPush.setSize();
            itemToPush.checkItemChanges(
                itemToPush.$item,
                itemToPush.item
            );
        } else { // push failed, reset changes
            switch (event.event.key) {
                case ActionKeys.DOWN:
                    itemToPush.$item.y--;
                    break;
                case ActionKeys.UP:
                    itemToPush.$item.y++;
                    break;
                case ActionKeys.LEFT:
                    itemToPush.$item.x++;
                    break;
                case ActionKeys.RIGHT:
                    itemToPush.$item.x--;
                    break;
            }
            push.restoreItems();
        }
        requestAnimationFrame(() => dragHandle.focus());
        push.destroy();
    }

    public handleBlockKeyboardResize(event: {event: KeyboardEvent; block: BlockModel}): void {
        const itemToResize = this.pageBuilderService.gridsterAPI.getItemComponent(event.block);
        const push = new GridsterPush(itemToResize);
        let direction: string;
        switch (event.event.key) {
            case ActionKeys.DOWN:
                direction = push.fromNorth;
                itemToResize.$item.rows++;
                break;
            case ActionKeys.UP:
                direction = push.fromSouth;
                itemToResize.$item.rows--;
                break;
            case ActionKeys.LEFT:
                direction = push.fromEast;
                itemToResize.$item.cols--;
                break;
            case ActionKeys.RIGHT:
                direction = push.fromWest;
                itemToResize.$item.cols++;
                break;
        }
        if (push.pushItems(direction)) {
            push.checkPushBack();
            push.setPushedItems();
            itemToResize.setSize(true);
            itemToResize.checkItemChanges(
                itemToResize.$item,
                itemToResize.item
            );
        } else {
            switch (event.event.key) {
                case ActionKeys.DOWN:
                    itemToResize.$item.rows--;
                    break;
                case ActionKeys.UP:
                    itemToResize.$item.rows++;
                    break;
                case ActionKeys.LEFT:
                    itemToResize.$item.cols++;
                    break;
                case ActionKeys.RIGHT:
                    itemToResize.$item.cols--;
                    break;
            }
            push.restoreItems();
        }
        push.destroy();
    }
}
